/**
 * A DATE OF BIRTH AS THREE TYPED PARTS (plan ISR, owner decision ISR-D2).
 *
 * The guest check-in asked for a date of birth through a native date input.
 * Its calendar opens on this month, so an adult paged back decades, and a
 * half-typed field gave '' without a word: on 2026-10-27 the owner filled every
 * name and none of three birth dates reached the server (PLAN-ISR-LOG C-1).
 *
 * Day and year are typed, the month is chosen by name, and every problem is
 * named before anything is sent. The value is a date KEY ('YYYY-MM-DD'), never
 * a Date: a birth date has no time zone, and the age is taken on the stay's
 * check-in day on the building's calendar.
 */

export interface BirthParts {
  day: string;
  month: string;
  year: string;
}

export const EMPTY_BIRTH: BirthParts = { day: '', month: '', year: '' };

export type AgeBand = 'adult' | 'minor_14_17' | 'minor_under_14';

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** The oldest a guest can be (the server's MAX_AGE_YEARS). */
export const MAX_AGE_YEARS = 120;

export type BirthProblem =
  | 'empty'
  | 'day_missing'
  | 'month_missing'
  | 'year_missing'
  | 'year_short'
  | 'not_a_day'
  | 'after_check_in'
  | 'too_old';

export const BIRTH_PROBLEM_WORDS: Record<BirthProblem, string> = {
  empty: 'Enter their date of birth.',
  day_missing: 'Add the day of the month.',
  month_missing: 'Choose the month.',
  year_missing: 'Add the year.',
  year_short: 'Enter the year with 4 digits, like 1987.',
  not_a_day: 'That date does not exist. Check the day and the month.',
  after_check_in: 'The date of birth cannot be after the check-in date.',
  too_old: 'Check the year.',
};

const digits = (s: string) => s.replace(/\D/g, '');
const pad2 = (n: number) => String(n).padStart(2, '0');
const daysIn = (y: number, m: number) => [31, (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];

/** Keep only what each box can hold: 2 digits for the day, 4 for the year. */
export const cleanDay = (s: string) => digits(s).slice(0, 2);
export const cleanYear = (s: string) => digits(s).slice(0, 4);

export const isBlank = (p: BirthParts) => !p.day.trim() && !p.month && !p.year.trim();

/**
 * The date key, or the first problem. `onDay` is the check-in day ('YYYY-MM-DD').
 */
export function checkBirth(p: BirthParts, onDay: string): { value: string } | { problem: BirthProblem } {
  if (isBlank(p)) return { problem: 'empty' };
  if (!digits(p.day)) return { problem: 'day_missing' };
  if (!p.month) return { problem: 'month_missing' };
  if (!digits(p.year)) return { problem: 'year_missing' };
  if (digits(p.year).length !== 4) return { problem: 'year_short' };
  const y = Number(digits(p.year));
  const m = Number(p.month);
  const d = Number(digits(p.day));
  if (!(m >= 1 && m <= 12) || d < 1 || d > daysIn(y, m)) return { problem: 'not_a_day' };
  const value = `${y}-${pad2(m)}-${pad2(d)}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(onDay) && value > onDay) return { problem: 'after_check_in' };
  const age = ageOn(value, onDay);
  if (age !== null && age > MAX_AGE_YEARS) return { problem: 'too_old' };

  return { value };
}

/** Whole years on `onYmd` (the server's ageOn: the birthday counts on its own month and day). */
export function ageOn(dobYmd: string, onYmd: string): number | null {
  const a = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dobYmd);
  const b = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onYmd);
  if (!a || !b) return null;
  let age = Number(b[1]) - Number(a[1]);
  if (Number(b[2]) < Number(a[2]) || (Number(b[2]) === Number(a[2]) && Number(b[3]) < Number(a[3]))) age -= 1;

  return age;
}

/** The band the server will give (src/stays/checkin/party.js ageBand), for the live count only. */
export function bandOf(dobYmd: string, onYmd: string): AgeBand | null {
  const age = ageOn(dobYmd, onYmd);
  if (age === null || age < 0 || age > MAX_AGE_YEARS) return null;
  if (age >= 18) return 'adult';
  if (age >= 14) return 'minor_14_17';

  return 'minor_under_14';
}
